import { defineStore } from 'pinia'
import http from '@/services/http'
import { useRootStore } from '@/stores/root'

// Port modulo Vuex `products` → Pinia.
// getters.user / getters.lastCustomer → useRootStore().user / .lastCustomer
export const useProductsStore = defineStore('products', {
  actions: {
    async fetchProductsList({
      searchText,
      category,
      subcategory,
      series,
      quantity,
      ranking1,
      ranking2,
      ranking3,
      ranking4,
      ranking5,
      cart,
      forceRefresh,
    }) {
      const rootStore = useRootStore()
      rootStore.setIsLoading(true)
      try {
        const response = await http.request(
          {
            method: 'get',
            path: '/products/search',
            params: Object.assign(
              {
                codmag: rootStore.user?.profile.codiceMagazzino,
                customerId: rootStore.lastCustomer.anacod,
              },
              searchText && { txtric: searchText },
              category ? { category } : { category: 0 },
              subcategory ? { subcategory } : { subcategory: 0 },
              series && { series },
              quantity && { quantity },
              ranking1 && { wclas1: ranking1 },
              ranking2 && { wclas2: ranking2 },
              ranking3 && { wclas3: ranking3 },
              ranking4 && { wclas4: ranking4 },
              ranking5 && { wclas5: ranking5 },
              cart && { numpro: cart },
            ),
          },
          forceRefresh,
        )
        rootStore.setIsLoading(false)
        return response
      } catch {
        rootStore.setIsLoading(false)
        return false
      }
    },
    async productInfo({ productId, customerId, loader = true, forceRefresh }) {
      const rootStore = useRootStore()
      if (loader) rootStore.setIsLoading(true)
      try {
        const response = await http.request(
          {
            method: 'get',
            path: '/products/basic',
            params: { codart: productId, customerid: customerId },
          },
          forceRefresh,
        )
        if (loader) rootStore.setIsLoading(false)
        return response
      } catch {
        if (loader) rootStore.setIsLoading(false)
        return false
      }
    },
    getProductMedia({ productId, tiprec, forceRefresh }) {
      return http.request(
        {
          method: 'get',
          path: '/products/media',
          params: { tiprec: tiprec ?? 'art', codart: productId },
        },
        forceRefresh,
      )
    },
    insertProductMedia({ productId, tagName, tiprec = 'ART' }) {
      const rootStore = useRootStore()
      return http.request({
        method: 'post',
        path: '/products/insertmedia',
        data: { keydoc: productId, tiprec, rifdoc: tagName, anacod: rootStore.lastCustomer.anacod },
      })
    },
    prepareDeleteProductMedia({ mediaId }) {
      return http.request({
        method: 'post',
        path: '/products/deleteMedia',
        data: { numpro: mediaId, tipazi: '' },
      })
    },
    deleteProductMedia({ mediaId }) {
      return http.request({
        method: 'post',
        path: '/products/deleteMedia',
        data: { numpro: mediaId, tipazi: 'D' },
      })
    },
    async productDetail({ productId, forceRefresh }) {
      const rootStore = useRootStore()
      rootStore.setIsLoading(true)
      try {
        const response = await http.request(
          {
            method: 'get',
            path: '/documents/productdetail',
            params: {
              codart: productId,
              codmag: rootStore.user?.profile.codiceMagazzino,
              codcli: rootStore.lastCustomer.anacod,
            },
          },
          forceRefresh,
        )
        rootStore.setIsLoading(false)
        return response
      } catch {
        rootStore.setIsLoading(false)
        return false
      }
    },
    async productsWarehouses({ forceRefresh }) {
      const rootStore = useRootStore()
      rootStore.setIsLoading(true)
      try {
        const response = await http.request(
          { method: 'get', path: '/products/warehouses' },
          forceRefresh,
        )
        rootStore.setIsLoading(false)
        return response
      } catch {
        rootStore.setIsLoading(false)
        return false
      }
    },
    async productsCategories({ forceRefresh }) {
      const rootStore = useRootStore()
      rootStore.setIsLoading(true)
      try {
        const response = await http.request(
          { method: 'get', path: '/products/categories' },
          forceRefresh,
        )
        rootStore.setIsLoading(false)
        return response
      } catch {
        rootStore.setIsLoading(false)
        return false
      }
    },
    async productsSubCategories({ forceRefresh, categoryCode }) {
      const rootStore = useRootStore()
      rootStore.setIsLoading(true)
      try {
        const response = await http.request(
          {
            method: 'get',
            path: '/products/subcategories',
            params: categoryCode && { categoryCode },
          },
          forceRefresh,
        )
        rootStore.setIsLoading(false)
        return response
      } catch {
        rootStore.setIsLoading(false)
        return false
      }
    },
    async productsSeries({ forceRefresh, categoryCode, subcategoryCode }) {
      const rootStore = useRootStore()
      rootStore.setIsLoading(true)
      try {
        const response = await http.request(
          {
            method: 'get',
            path: '/products/series',
            params: { categoryCode, subcategoryCode },
          },
          forceRefresh,
        )
        rootStore.setIsLoading(false)
        return response
      } catch {
        rootStore.setIsLoading(false)
        return false
      }
    },
    async productAvailabilities({ productId, forceRefresh }) {
      const rootStore = useRootStore()
      rootStore.setIsLoading(true)
      try {
        const response = await http.request(
          { method: 'get', path: '/products/availability', params: { codart: productId } },
          forceRefresh,
        )
        rootStore.setIsLoading(false)
        return response
      } catch {
        rootStore.setIsLoading(false)
        return false
      }
    },
    async productSales({ productId, date = 'null', forceRefresh }) {
      const rootStore = useRootStore()
      rootStore.setIsLoading(true)
      try {
        const response = await http.request(
          {
            method: 'get',
            path: '/products/sales',
            params: { codart: productId, year: date },
          },
          forceRefresh,
        )
        rootStore.setIsLoading(false)
        return response
      } catch {
        rootStore.setIsLoading(false)
        return false
      }
    },
    async productCrossReferences({ productId, mag, custId, forceRefresh }) {
      const rootStore = useRootStore()
      rootStore.setIsLoading(true)
      try {
        const response = await http.request(
          {
            method: 'get',
            path: '/products/references',
            params: { codart: productId, codMag: mag, customerId: custId },
          },
          forceRefresh,
        )
        rootStore.setIsLoading(false)
        return response
      } catch {
        rootStore.setIsLoading(false)
        return false
      }
    },
    async productReplacer({ productId, mag, codCli, tipdis, forceRefresh }) {
      const rootStore = useRootStore()
      rootStore.setIsLoading(true)
      try {
        const response = await http.request(
          {
            method: 'get',
            path: '/products/billArticles',
            params: { codart: productId, codmag: mag, codcli: codCli, tipdis },
          },
          forceRefresh,
        )
        rootStore.setIsLoading(false)
        return response
      } catch {
        rootStore.setIsLoading(false)
        return false
      }
    },
    async productSupplierOrders({ productId, storeCode, forceRefresh }) {
      const rootStore = useRootStore()
      rootStore.setIsLoading(true)
      try {
        const response = await http.request(
          {
            method: 'get',
            path: '/products/orders/supplier',
            params: { codmag: storeCode, codart: productId },
          },
          forceRefresh,
        )
        rootStore.setIsLoading(false)
        return response
      } catch {
        rootStore.setIsLoading(false)
        return false
      }
    },
    async productCustomerOrders({ productId, storeCode, forceRefresh }) {
      const rootStore = useRootStore()
      rootStore.setIsLoading(true)
      try {
        const response = await http.request(
          {
            method: 'get',
            path: '/products/orders/customer',
            params: { codmag: storeCode, codart: productId },
          },
          forceRefresh,
        )
        rootStore.setIsLoading(false)
        return response
      } catch {
        rootStore.setIsLoading(false)
        return false
      }
    },
    async getAvaiblityProductInSubsidiary({ productId, forceRefresh }) {
      const rootStore = useRootStore()
      rootStore.setIsLoading(true)
      try {
        const response = await http.request(
          { method: 'get', path: '/products/availabilitySub', params: { codart: productId } },
          forceRefresh,
        )
        rootStore.setIsLoading(false)
        return response
      } catch {
        rootStore.setIsLoading(false)
        return false
      }
    },
    async getNoteTypes({ productId, language, forceRefresh }) {
      const rootStore = useRootStore()
      rootStore.setIsLoading(true)
      try {
        const response = await http.request(
          {
            method: 'get',
            path: '/products/getNoteTypes',
            params: { codart: productId, codlng: language },
          },
          forceRefresh,
        )
        rootStore.setIsLoading(false)
        return response
      } catch {
        rootStore.setIsLoading(false)
        return false
      }
    },
    async productNote({ productId, language, tipnot = 'T', loader = true, forceRefresh }) {
      const rootStore = useRootStore()
      if (loader) rootStore.setIsLoading(true)
      try {
        const response = await http.request(
          {
            method: 'get',
            path: '/products/productNote',
            params: { codart: productId, tipnot, codlng: language },
          },
          forceRefresh,
        )
        if (loader) rootStore.setIsLoading(false)
        return response
      } catch {
        if (loader) rootStore.setIsLoading(false)
        return false
      }
    },
    async getProductsAdvancedFilters({ category1, category2, category3, category4, forceRefresh }) {
      const rootStore = useRootStore()
      rootStore.setIsLoading(true)
      try {
        const response = await http.request(
          {
            method: 'get',
            path: '/products/webCategories',
            params: category1
              ? Object.assign(
                  { class1: category1 },
                  category2 && { class2: category2 },
                  category3 && { class3: category3 },
                  category4 && { class4: category4 },
                )
              : {},
          },
          forceRefresh,
        )
        rootStore.setIsLoading(false)
        return response
      } catch {
        rootStore.setIsLoading(false)
        return false
      }
    },
  },
})
